import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import Modal from '../components/Modal';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('tr-TR') : '';
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Nakit' },
  { value: 'bank', label: 'Banka' },
  { value: 'credit', label: 'Kredi' },
  { value: 'transfer', label: 'Havale/EFT' },
];

// Reusable Payment Modal component — exported for use in other pages
export function PaymentModal({ entityType, entityId, entityName, totalAmount, direction, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    amount: '', paymentMethod: 'cash', bankAccountId: '',
    paymentDate: new Date().toISOString().split('T')[0], notes: '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existingPayments, setExistingPayments] = useState([]);

  useEffect(() => {
    api.get('/settings/bank-accounts').then(r => setBankAccounts(r.data.data)).catch(() => {});
    api.get(`/payments/${entityType}/${entityId}`).then(r => setExistingPayments(r.data.data)).catch(() => {});
  }, [entityType, entityId]);

  const totalPaid = existingPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const remaining = (parseFloat(totalAmount) || 0) - totalPaid;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      return toast.error(t('payments.invalidAmount'));
    }
    setSaving(true);
    try {
      await api.post('/payments', {
        entityType, entityId, direction,
        amount: form.amount, paymentMethod: form.paymentMethod,
        bankAccountId: form.bankAccountId || null,
        paymentDate: form.paymentDate, notes: form.notes,
      });
      toast.success(t('payments.saved'));
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Payment frontend error:', err.response?.data);
      toast.error(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t('payments.saveFailed')
      );
    } finally { setSaving(false); }
  };

  const title = direction === 'in' ? t('payments.collectPayment') : t('payments.makePayment');

  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ background: '#f0f7ee', borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: '0.82rem', color: '#666' }}>{entityName}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: '0.82rem' }}>{t('payments.totalAmount')}: <b>{fmt(totalAmount)}</b></span>
          <span style={{ fontSize: '0.82rem' }}>{t('payments.remaining')}: <b style={{ color: remaining > 0 ? '#e74c3c' : '#27ae60' }}>{fmt(remaining)}</b></span>
        </div>
        {existingPayments.length > 0 && (
          <div style={{ marginTop: 8, borderTop: '1px solid #d4e4cc', paddingTop: 8 }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: 4 }}>{t('payments.previousPayments')}:</div>
            {existingPayments.map(p => (
              <div key={p.id} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between' }}>
                <span>{fmtDate(p.payment_date)} · {PAYMENT_METHODS.find(m => m.value === p.payment_method)?.label}</span>
                <span style={{ fontWeight: 700 }}>{fmt(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>{t('payments.amount')} *</label>
            <input type="number" step="0.01" value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder={remaining > 0 ? remaining.toFixed(2) : '0.00'} required />
          </div>
          <div className="form-group">
            <label>{t('payments.method')}</label>
            <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        {(form.paymentMethod === 'bank' || form.paymentMethod === 'transfer') && bankAccounts.length > 0 && (
          <div className="form-group">
            <label>{t('payments.bankAccount')}</label>
            <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)}>
              <option value="">--</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label>{t('payments.date')}</label>
            <input type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t('payments.notes')}</label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
            onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Lump-sum settle across a party's open items (oldest-first). Handles both
// "pay all" (leave the full amount) and "pay some" (reduce the amount).
function PartySettleModal({ partyName, direction, items, onClose, onSaved }) {
  const totalRemaining = items.reduce((s, i) => s + (parseFloat(i.remaining) || 0), 0);
  const [form, setForm] = useState({
    amount: totalRemaining.toFixed(2), paymentMethod: 'cash', bankAccountId: '',
    paymentDate: new Date().toISOString().split('T')[0], notes: '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/settings/bank-accounts').then(r => setBankAccounts(r.data.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Preview how the entered amount would spread across items, oldest first.
  const sorted = [...items].sort((a, b) => new Date(a.date) - new Date(b.date));
  let left = parseFloat(form.amount) || 0;
  const preview = sorted.map(it => {
    const alloc = Math.min(parseFloat(it.remaining) || 0, Math.max(left, 0));
    left -= alloc;
    return { ...it, alloc: Math.round(alloc * 100) / 100 };
  });

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error('Geçerli bir tutar girin');
    setSaving(true);
    try {
      const r = await api.post('/payments/party/settle', {
        direction, amount: amt,
        entities: items.map(i => ({ type: i.entityType, id: i.id })),
        paymentMethod: form.paymentMethod, bankAccountId: form.bankAccountId || null,
        paymentDate: form.paymentDate, notes: form.notes,
      });
      toast.success(`${fmt(r.data.paidTotal)} · ${r.data.count} kalem ${direction === 'in' ? 'tahsil edildi' : 'ödendi'}`);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const isIn = direction === 'in';
  return (
    <Modal title={`${partyName} · ${isIn ? 'Alacak Tahsil' : 'Toplu Ödeme'}`} onClose={onClose}>
      <div style={{ background: '#f0f7ee', borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.82rem' }}>{isIn ? 'Toplam Alacak' : 'Toplam Borç'} ({items.length} kalem)</span>
          <b style={{ color: isIn ? '#27ae60' : '#e74c3c' }}>{fmt(totalRemaining)}</b>
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="form-group">
          <label>{isIn ? 'Tahsil Tutarı' : 'Ödeme Tutarı'} (₺) *</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)}
              style={{ flex: 1 }} required />
            <button type="button" onClick={() => set('amount', totalRemaining.toFixed(2))}
              style={{ background: 'var(--green-pale)', border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', fontWeight: 700, cursor: 'pointer', color: 'var(--green-mid)' }}>
              Tümü
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: '#7a9472', marginTop: 4 }}>En eski borçtan başlanarak dağıtılır.</div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Yöntem</label>
            <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Tarih</label>
            <input type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
          </div>
        </div>
        {(form.paymentMethod === 'bank' || form.paymentMethod === 'transfer') && bankAccounts.length > 0 && (
          <div className="form-group">
            <label>Banka Hesabı</label>
            <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)}>
              <option value="">--</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label>Not</label>
          <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Örn: Nisan ayı işçilik" />
        </div>

        {/* Allocation preview */}
        <div style={{ background: '#fafafa', borderRadius: 8, padding: 8, marginBottom: 10, maxHeight: 160, overflowY: 'auto' }}>
          <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 4 }}>Dağıtım:</div>
          {preview.map(it => (
            <div key={`${it.entityType}-${it.id}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '2px 0', opacity: it.alloc > 0 ? 1 : 0.4 }}>
              <span>{fmtDate(it.date)} · {it.title}</span>
              <span style={{ fontWeight: 700, color: it.alloc >= (parseFloat(it.remaining) || 0) - 0.01 ? '#27ae60' : '#e67e22' }}>
                {fmt(it.alloc)}{it.alloc > 0 && it.alloc < (parseFloat(it.remaining) || 0) - 0.01 ? ` / ${fmt(it.remaining)}` : ''}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={onClose}>İptal</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
            {saving ? 'Kaydediliyor...' : (isIn ? 'Tahsil Et' : 'Öde')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Main PaymentsPage
export default function PaymentsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('parties'); // 'parties' | 'in' (receivables) | 'out' (payables)
  const [summary, setSummary] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [parties, setParties] = useState([]);
  const [expandedParty, setExpandedParty] = useState(null);
  const [settleTarget, setSettleTarget] = useState(null); // { partyName, direction, items }

  const loadSummary = useCallback(() => {
    api.get('/payments/summary').then(r => setSummary(r.data.data)).catch(console.error);
  }, []);

  const loadParties = useCallback(() => {
    setLoading(true);
    api.get('/payments/parties')
      .then(r => setParties(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadPending = useCallback(() => {
    if (activeTab === 'parties') return;
    setLoading(true);
    if (activeTab === 'in') {
      // Receivables: sales with pending/partial
      api.get('/sales').then(r => {
        const pending = r.data.data.filter(i => i.payment_status === 'pending' || i.payment_status === 'partial');
        setPendingItems(pending.map(i => ({
          ...i, _entityType: 'sale', _name: i.product_name + (i.contact_name ? ` · ${i.contact_name}` : ''),
          _date: i.sale_date, _direction: 'in',
        })));
      }).catch(console.error).finally(() => setLoading(false));
    } else {
      // Payables: purchases + labour with pending/partial
      Promise.all([
        api.get('/purchases'),
        api.get('/farm/labour'),
      ]).then(([pRes, lRes]) => {
        const purchases = (pRes.data.data || [])
          .filter(i => i.payment_status === 'pending' || i.payment_status === 'partial')
          .map(i => ({
            ...i, _entityType: 'purchase', _name: i.item_name + (i.contact_name ? ` · ${i.contact_name}` : ''),
            _date: i.transaction_date, _direction: 'out',
          }));
        const labour = (lRes.data.data || [])
          .filter(i => i.payment_status === 'pending' || i.payment_status === 'partial')
          .map(i => ({
            ...i, _entityType: 'labour', _name: `${i.worker_name} · ${i.work_type}`,
            _date: i.work_date, _direction: 'out',
          }));
        setPendingItems([...purchases, ...labour]);
      }).catch(console.error).finally(() => setLoading(false));
    }
  }, [activeTab]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (activeTab === 'parties') loadParties();
    else loadPending();
  }, [activeTab, loadParties, loadPending]);

  const handlePaymentSaved = () => {
    loadSummary();
    if (activeTab === 'parties') loadParties();
    else loadPending();
  };

  return (
    <div>
      <h2 className="section-title">{t('payments.title')}</h2>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div className="card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>{t('payments.outstandingReceivables')}</div>
            <div style={{ fontWeight: 800, color: '#27ae60', fontSize: '1.1rem' }}>{fmt(summary.outstandingReceivables)}</div>
          </div>
          <div className="card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>{t('payments.outstandingPayables')}</div>
            <div style={{ fontWeight: 800, color: '#e74c3c', fontSize: '1.1rem' }}>{fmt(summary.outstandingPayables)}</div>
          </div>
          <div className="card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>{t('payments.monthCollected')}</div>
            <div style={{ fontWeight: 800, color: '#2d5a27', fontSize: '1.1rem' }}>{fmt(summary.monthIn)}</div>
          </div>
          <div className="card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>{t('payments.monthPaid')}</div>
            <div style={{ fontWeight: 800, color: '#8b5e3c', fontSize: '1.1rem' }}>{fmt(summary.monthOut)}</div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="settings-tabs" style={{ marginBottom: 12 }}>
        <button className={`settings-tab${activeTab === 'parties' ? ' active' : ''}`}
          onClick={() => { setActiveTab('parties'); setExpandedParty(null); }}>
          👥 Cari Hesap
        </button>
        <button className={`settings-tab${activeTab === 'in' ? ' active' : ''}`}
          onClick={() => setActiveTab('in')}>
          {t('payments.receivables')}
        </button>
        <button className={`settings-tab${activeTab === 'out' ? ' active' : ''}`}
          onClick={() => setActiveTab('out')}>
          {t('payments.payables')}
        </button>
      </div>

      {/* Parties (cari hesap) view — who owes us / whom we owe, grouped by party */}
      {activeTab === 'parties' ? (
        <div className="card">
          {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
            : parties.length === 0 ? (
              <div className="empty-state"><div className="icon">👥</div><p>Açık bakiye yok</p></div>
            ) : parties.map(p => {
              const open = expandedParty === p.name;
              return (
                <div key={p.name} style={{ borderBottom: '1px solid #f2f2f2' }}>
                  <div onClick={() => setExpandedParty(open ? null : p.name)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 2px', cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#1c3a1c' }}>{p.name}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                        {p.receivable > 0 && <span style={{ fontSize: '0.72rem', color: '#27ae60', fontWeight: 700 }}>Alacak: {fmt(p.receivable)}</span>}
                        {p.payable > 0 && <span style={{ fontSize: '0.72rem', color: '#e74c3c', fontWeight: 700 }}>Borç: {fmt(p.payable)}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, color: p.net >= 0 ? '#27ae60' : '#e74c3c' }}>
                        {p.net >= 0 ? '+' : ''}{fmt(p.net)}
                      </div>
                      <div style={{ fontSize: '0.66rem', color: '#aaa' }}>{p.net >= 0 ? 'net alacak' : 'net borç'} {open ? '▲' : '▼'}</div>
                    </div>
                  </div>

                  {open && (
                    <div style={{ padding: '0 0 10px 6px' }}>
                      {/* Bulk actions — pay all / pay some against the party's open items */}
                      <div style={{ display: 'flex', gap: 8, margin: '4px 0 10px' }}>
                        {p.payable > 0 && (
                          <button onClick={() => setSettleTarget({ partyName: p.name, direction: 'out', items: p.items.filter(i => i.direction === 'out') })}
                            style={{ flex: 1, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '7px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                            💳 Borçları Öde ({fmt(p.payable)})
                          </button>
                        )}
                        {p.receivable > 0 && (
                          <button onClick={() => setSettleTarget({ partyName: p.name, direction: 'in', items: p.items.filter(i => i.direction === 'in') })}
                            style={{ flex: 1, background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8, padding: '7px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                            💰 Alacakları Tahsil Et ({fmt(p.receivable)})
                          </button>
                        )}
                      </div>
                      {p.items.map(it => (
                        <div key={`${it.entityType}-${it.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '1px solid #f7f7f7' }}>
                          <span>{it.entityType === 'sale' ? '💰' : it.entityType === 'purchase' ? '🛒' : '👷'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{it.title}</div>
                            <div style={{ fontSize: '0.7rem', color: '#888' }}>
                              {fmtDate(it.date)} · Kalan {fmt(it.remaining)}
                              {it.status === 'partial' && ` (Toplam ${fmt(it.total)})`}
                            </div>
                          </div>
                          <button
                            onClick={() => setPaymentTarget({
                              id: it.id, _entityType: it.entityType, _direction: it.direction,
                              _name: `${p.name} · ${it.title}`, total_amount: it.total,
                            })}
                            style={{
                              background: it.direction === 'in' ? '#27ae60' : '#e74c3c',
                              color: '#fff', border: 'none', borderRadius: 8,
                              padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                            }}>
                            {it.direction === 'in' ? t('payments.collect') : t('payments.pay')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ) : (

      /* Pending items list */
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : pendingItems.length === 0 ? (
            <div className="empty-state">
              <div className="icon">{activeTab === 'in' ? '💰' : '💳'}</div>
              <p>{t('payments.noPending')}</p>
            </div>
          ) : pendingItems.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item._name}</div>
                <div className="txn-meta">
                  {fmtDate(item._date)}
                  {item._entityType === 'labour' && ` · ${item.num_workers} kişi · ${item.days} gün`}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={`badge badge-${item.payment_status}`}>
                    {item.payment_status === 'pending' ? t('common.pending') : t('common.partial')}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#aaa' }}>
                    {item._entityType === 'purchase' ? '🛒' : item._entityType === 'sale' ? '💰' : '👷'}
                  </span>
                </div>
                {item.payment_status === 'partial' && (
                  <div className="txn-meta" style={{ marginTop: 3 }}>
                    Toplam: {fmt(item.total_amount)} · Ödenen: {fmt(item.amount_paid)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                {item.payment_status === 'partial' && (
                  <span style={{ fontSize: '0.66rem', color: '#aaa', textDecoration: 'line-through' }}>{fmt(item.total_amount)}</span>
                )}
                <span className={`txn-amount ${activeTab === 'in' ? 'income' : 'expense'}`}>
                  {fmt(item.payment_status === 'partial'
                    ? parseFloat(item.total_amount) - parseFloat(item.amount_paid || 0)
                    : item.total_amount)}
                </span>
                {item.payment_status === 'partial' && (
                  <span style={{ fontSize: '0.62rem', color: '#aaa', fontWeight: 700 }}>Kalan</span>
                )}
                <button
                  onClick={() => setPaymentTarget(item)}
                  style={{
                    marginTop: 4,
                    background: activeTab === 'in' ? '#27ae60' : '#e74c3c',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  }}>
                  {activeTab === 'in' ? t('payments.collect') : t('payments.pay')}
                </button>
              </div>
            </div>
          ))}
      </div>
      )}

      {/* Payment modal */}
      {paymentTarget && (
        <PaymentModal
          entityType={paymentTarget._entityType}
          entityId={paymentTarget.id}
          entityName={paymentTarget._name}
          totalAmount={paymentTarget.total_amount}
          direction={paymentTarget._direction}
          onClose={() => setPaymentTarget(null)}
          onSaved={handlePaymentSaved}
        />
      )}

      {/* Bulk party settle modal */}
      {settleTarget && settleTarget.items.length > 0 && (
        <PartySettleModal
          partyName={settleTarget.partyName}
          direction={settleTarget.direction}
          items={settleTarget.items}
          onClose={() => setSettleTarget(null)}
          onSaved={handlePaymentSaved}
        />
      )}
    </div>
  );
}
